import loadjs from "loadjs"
import { replace } from "redux-first-history"
import { call, put, select } from "redux-saga/effects"

import { history } from "app-store-v2/index"
import { applyDefaultsToHandwritingImg, thresholdSettings } from "helpers/image"
import IndexedDBCache from "helpers/indexeddb-cache"
import StorageHelper from "helpers/local-storage-cache"

import { updateChoice } from "../actions"

async function storeOriginalImage(file, pref, ratioWidth, ratioHeight) {
  await IndexedDBCache.storeBlob(
    `image-${pref}-${ratioWidth}-${ratioHeight}`,
    file
  )
  // Clear any stale crop-position state from a previous upload to the same slot
  StorageHelper.remove(`cropper-${pref}-${ratioWidth}-${ratioHeight}`)
}

export default function* uploadUserImage(action) {
  const state = yield select()
  if (!state.wizardUI.prefStore) {
    return
  }

  let file_input = action.fileSource
  let pref = action.pref
  let isHandwriting = action.isHandwriting

  var file = null
  if (
    !file &&
    file_input.dataTransfer &&
    file_input.dataTransfer.files &&
    file_input.dataTransfer.files[0]
  ) {
    file = file_input.dataTransfer.files[0]
  }
  if (!file && file_input.files && file_input.files[0]) {
    file = file_input.files[0]
  }

  if (!file) {
    return false
  }

  if (!(file.type.indexOf("image") !== -1)) {
    alert("You can only upload an image. Please try again.")
    return false
  }
  if (!/(jpe?g|png|heif)$/i.test(file.type)) {
    alert("File format not supported. Please use JPEG or PNG or HEIC.")
    return false
  }
  // Convert heic file to png file
  if (file.type?.includes("heif")) {
    file = yield call(heicConvert, file)
  }
  if (file.size > 15000000) {
    alert(
      "The image you have uploaded is too large. Please upload an image smaller than 15 MB"
    )
    return false
  }

  let base64
  try {
    const { dataUrl } = yield call(readToDataUrl, file, isHandwriting)
    base64 = dataUrl

    yield call(
      storeOriginalImage,
      file,
      pref,
      state.wizard.data[pref].cropper.aspect_ratio_width,
      state.wizard.data[pref].cropper.aspect_ratio_height
    )

    if (state.choices[`orig_${pref}`] && state.choices[`orig_${pref}`].value) {
      yield put(updateChoice(`orig_${pref}`, { value: null }))
    }
  } catch (error) {
    alert(error)
    return false
  }

  try {
    const updatedChoice = {}
    // Handwriting sku: we applied the grey scale on the original upload img
    if (isHandwriting) {
      updatedChoice.upload_image = base64
      updatedChoice.threshold_value = thresholdSettings.MEDIUM
      base64 = yield call(applyDefaultsToHandwritingImg, base64)
    }
    updatedChoice.cropper_image = base64
    yield put(updateChoice(pref, updatedChoice))

    if (!state.browser.is.mobile && (pref === "img1" || pref === "img2")) {
      yield put(
        replace(
          `${history.location.pathname}${history.location.search}${history.location.hash}`
        )
      )
    }

    if (action.uploadCallback) {
      action.uploadCallback()
    }
  } catch (e) {
    console.error(e)
    alert(
      "There was a problem uploading your image. Please try again or use a different image."
    )
  }
}

function readToDataUrl(file, isHandwriting) {
  return new Promise(function (resolve, reject) {
    var reader = new FileReader()
    reader.onload = function (e) {
      let dataUrl = e.target.result
      if (isHandwriting) {
        const img = new Image()
        img.src = dataUrl
        img.onload = () => {
          const canvasSize = Math.max(img.naturalWidth, img.naturalHeight)
          const scaleFactor = getScaleFactor(canvasSize)
          if (scaleFactor > 1) {
            const canvas = document.createElement("canvas")
            const ctx = canvas.getContext("2d")
            const canvasW = img.naturalWidth / scaleFactor
            const canvasH = img.naturalHeight / scaleFactor
            canvas.width = canvasW
            canvas.height = canvasH
            ctx.drawImage(img, 0, 0, canvasW, canvasH)
            dataUrl = canvas.toDataURL()
            resolve({ dataUrl })
          } else {
            resolve({ dataUrl })
          }
        }
        img.onerror = (err) => reject(err)
      } else {
        resolve({ dataUrl })
      }
    }
    reader.onerror = function () {
      return reject()
    }
    reader.readAsDataURL(file)
  })
}

function getScaleFactor(size) {
  if (!size || size < 0) return 0
  if (size < 2000) return 1
  return size / 2000
}

function heicConvert(file) {
  return new Promise((resolve, reject) => {
    if (!window.isLoadHeic2Any) {
      loadjs(
        [
          "https://cdnjs.cloudflare.com/ajax/libs/heic2any/0.0.3/heic2any.min.js",
        ],
        "heic2any"
      )
    }
    loadjs.ready("heic2any", function () {
      window.isLoadHeic2Any = true
      // eslint-disable-next-line no-undef
      heic2any({
        blob: file,
        quality: 1,
        toType: "image/png",
      })
        .then((result) => {
          return resolve(result)
        })
        .catch((e) => {
          console.error(e)
          return reject()
        })
    })
  })
}
